import React, { useState, useCallback } from "react";
import { Card, List, Drawer, Input, Button, Avatar, Typography } from "antd";
import {
  ArrowRightOutlined,
  CreditCardOutlined,
  PlusCircleOutlined,
  BankOutlined,
  SwapOutlined,
  ReconciliationOutlined,
  DownOutlined,
  CloseOutlined,
  LeftOutlined,
  GiftOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import debounce from "lodash.debounce";

import SendMoneyConfirm from "./SendMoneyConfirm";
import RequestMoneyConfirm from "./RequestMoneyConfirm";
import MoneyRequestSuccess from "./MoneyRequestSuccess";
import MoneyRequests from "./MoneyRequests";
import { searchCustomers, sendMoney } from "../../../api/UserAPI";
import { useEffect } from "react";
import SendMoneySuccess from "./SendMoneySuccess";
import AuthService from "../../../session/AuthService";
import { useGlobalUI } from "../../../context/GlobalUIProvider";
import { createMoneyRequest } from "../../../api/WalletApi";
import WalletList from "./WalletList";

const { Text } = Typography;

// ----- Constants -----
const paymentOptions = [
  {
    key: "payany1",
    label: "Payany1",
    icon: <CreditCardOutlined style={{ fontSize: 20, color: "#8b96a9" }} />,
  },
  {
    key: "top-up",
    label: "Wallet top up",
    icon: <PlusCircleOutlined style={{ fontSize: 20, color: "#8b96a9" }} />,
  },
  {
    key: "wallet-to-bank",
    label: "Wallet to bank",
    icon: <BankOutlined style={{ fontSize: 20, color: "#8b96a9" }} />,
  },
  {
    key: "wallets",
    label: "Wallets",
    icon: <WalletOutlined style={{ fontSize: 20, color: "#8b96a9" }} />,
  },
  {
    key: "request-money",
    label: "Request money",
    icon: <SwapOutlined style={{ fontSize: 20, color: "#8b96a9" }} />,
  },
  {
    key: "view-payment-requests",
    label: "View payment requests",
    icon: <ReconciliationOutlined style={{ fontSize: 20, color: "#8b96a9" }} />,
  },
  {
    key: "claim-bonus",
    label: "Claim Bonus",
    icon: <GiftOutlined style={{ fontSize: 20, color: "#52c41a" }} />, // green gift icon
  },
];

// ----- Utility functions -----
const calculateTierFee = (flatFee, percentage, amount) => {
  if (flatFee > 0) return flatFee;
  if (percentage > 0) return (amount * percentage) / 100;
  return 0;
};

const calculateTransferFee = (config, amount) => {
  if (!config) return 0;

  if (config.flatConfig) return config.transferFee || 0;

  if (config.percentageConfig) {
    const min = config.percentageMinimumFee || 0;
    const max = config.percentageMaximumFee || Infinity;
    if (amount >= min && amount <= max) {
      return (amount * (config.percentageStandardRate || 0)) / 100;
    }
    return (amount * 10) / 100;
  }

  if (config.tierConfigEnabled) {
    const {
      tier1MinAmount,
      tier1MaxAmount,
      tier1FlatFee,
      tier1Percentage,
      tier2MinAmount,
      tier2MaxAmount,
      tier2FlatFee,
      tier2Percentage,
      tier3MinAmount,
      tier3FlatFee,
      tier3Percentage,
    } = config;

    if (amount >= tier1MinAmount && amount <= tier1MaxAmount)
      return calculateTierFee(tier1FlatFee, tier1Percentage, amount);
    if (amount >= tier2MinAmount && amount <= tier2MaxAmount)
      return calculateTierFee(tier2FlatFee, tier2Percentage, amount);
    if (amount >= tier3MinAmount)
      return calculateTierFee(tier3FlatFee, tier3Percentage, amount);
  }

  return 0;
};

// ----- Main Component -----
const PaymentScreen = ({
  userDetails,
  walletDetails,
  walletList,
  settingDetails,
  customerType,
  getUserDetails,
  setParentTab,
}) => {
  const [activeTab, setActiveTab] = useState("home");
  const [drawerVisible, setDrawerVisible] = useState("");
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [showNext, setShowNext] = useState("");
  const [formData, setFormData] = useState({
    description: "",
    category: "",
    payee: null,
    amount: "",
    requestReason: "",
  });

  const { showLoader, hideLoader, message } = useGlobalUI();

  useEffect(() => {
    if (activeTab === "home") {
      setFormData({
        description: "",
        category: "",
        payee: null,
        amount: "",
        requestReason: "",
      });
    }
  }, [activeTab === "home"]);

  // ----- Handlers -----
  const handlePaymentOptionClick = (key) => {
    console.log("key", key);
    if (key === "top-up" || key === "claim-bonus") {
      setParentTab(key);
    } else if (key === "payany1") {
      setActiveTab("payany1");
    } else {
      setActiveTab(key);
    }
  };

  const handleDrawerClose = () => setDrawerVisible("");

  const handlePayeeSelect = (payee) => {
    console.log("payee", payee);
    setSelectedPayee(payee);
    setFormData((prev) => ({ ...prev, payee }));
    handleDrawerClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = (action) => {
    if (action === "sendMoney") {
      // Manual validations
      const errors = [];

      if (!formData.payee || !formData.payee.name) {
        errors.push("Please select a payee");
      }

      if (!formData.description || formData.description.trim() === "") {
        errors.push("Please enter a description");
      }

      if (!formData.category || formData.category.trim() === "") {
        errors.push("Please select a category");
      }

      if (!formData.amount || Number(formData.amount) <= 0) {
        errors.push("Please enter a valid amount");
      }

      if (errors.length > 0) {
        // Show first error (or use notification/message for multiple)
        message.error(errors[0]); // AntD message
        return;
      }

      // If all validations pass, proceed with the action

      setShowNext(action);
      setActiveTab("");
    } else if (action === "requestMoney") {
      const errors = [];

      if (!formData.payee || !formData.payee.name) {
        errors.push("Please select a payee");
      }

      if (!formData.requestReason || formData.requestReason.trim() === "") {
        errors.push("Please enter a request reason");
      }

      if (!formData.amount || Number(formData.amount) <= 0) {
        errors.push("Please enter a valid amount");
      }

      if (errors.length > 0) {
        // Show first error (or use notification/message for multiple)
        message.error(errors[0]); // AntD message
        return;
      }

      setShowNext(action);
      setActiveTab("");
    } else {
      setShowNext(action);
      setActiveTab("");
    }
  };

  const getCustomerBySearch = async (value) => {
    if (!value) return setCustomerList([]);
    try {
      const response = await searchCustomers({ search: value });
      if (response.data.status) setCustomerList(response.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  const debouncedSearch = useCallback(debounce(getCustomerBySearch, 500), []);

  // ----- Renderers -----
  const renderMainMenu = () => (
    <Card style={{ borderRadius: 16 }}>
      <h2 style={{ fontWeight: 700, fontSize: 25, color: "#2e3a59" }}>
        Payments
      </h2>
      <List
        itemLayout="horizontal"
        dataSource={paymentOptions}
        renderItem={(item) => (
          <List.Item
            onClick={() => handlePaymentOptionClick(item.key)}
            style={{ cursor: "pointer" }}
            actions={[
              <ArrowRightOutlined
                key="arrow"
                style={{ color: "#b0b9c9", fontSize: 18 }}
              />,
            ]}
          >
            <List.Item.Meta
              avatar={item.icon}
              title={
                <span style={{ color: "#435072", fontWeight: 600 }}>
                  {item.label}
                </span>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );

  const openDrawer = (value) => setDrawerVisible(value);

  const handleSendMoney = (mobile, amount) => {
    const availableBalance = parseFloat(walletDetails.totalBalance);
    const enteredAmount = parseFloat(amount);

    if (enteredAmount > availableBalance) {
      message.error("Insufficient Balance");
      return;
    }

    showLoader();
    sendMoney({
      id: AuthService.getAuthId(),
      mobileNo: mobile,
      amountSend: enteredAmount,
    }).then((res) => {
      hideLoader();
      if (res.data.status === 1) {
        // message.success("Transaction Successful.");
        setActiveTab("sendMoneySuccess");
        setShowNext("");
        getUserDetails();
      } else {
        message.error(res.data.message);
      }
    });
  };

  const moneyRequestSent = () => {
    let obj = {
      toUserId: selectedPayee.id,
      amount: formData.amount,
    };
    createMoneyRequest(obj).then((response) => {
      if (response.data.status === 1) {
        setShowNext("");
        setActiveTab("requestMoney");
      } else {
        message.error(response.data.message);
      }
    });
  };

  const renderPayany1Form = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#f9f9f9",
      }}
    >
      <Card
        style={{
          maxWidth: 430,
          margin: "8px auto",
          borderRadius: 18,
          flex: 1,
        }}
      >
        <LeftOutlined
          style={{ fontSize: 18, cursor: "pointer", marginRight: 12 }}
          onClick={() => setActiveTab("home")}
        />

        {/* Header */}
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 18 }}>
          Money from
        </div>

        {/* Wallet Info */}
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 14 }}
        >
          <Avatar size={40}>{userDetails.name?.[0]}</Avatar>
          <span style={{ marginLeft: 8, fontWeight: 500 }}>
            Personal wallet
          </span>
          <span style={{ marginLeft: "auto", fontWeight: 500 }}>
            {userDetails.nationality === "Indian" ? "₹" : "$"}{" "}
            {parseFloat(walletDetails.totalBalance).toFixed(2)}
          </span>
        </div>
        <hr />

        {/* Money To */}
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 18 }}>
          Money to
        </div>
        <div
          onClick={() => openDrawer("payee")}
          style={{
            border: "1px solid #e6eaf0",
            borderRadius: 8,
            padding: 10,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            marginBottom: 16,
          }}
        >
          <span style={{ flex: 1, color: formData.payee ? "#222" : "#bbb" }}>
            {formData.payee ? formData.payee.name : "Phone number"}
          </span>
          <DownOutlined />
        </div>
        <br />

        {/* Inputs */}
        <Input
          placeholder="Description"
          bordered={false}
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          style={{
            marginBottom: 14,
            borderRadius: 12,
            background: "#f5f5f5",
            padding: "10px 14px",
          }}
        />
        <Input
          placeholder="Expense category"
          bordered={false}
          value={formData.category}
          onChange={(e) => handleChange("category", e.target.value)}
          style={{
            marginBottom: 14,
            borderRadius: 12,
            background: "#f5f5f5",
            padding: "10px 14px",
          }}
        />
      </Card>

      {/* Fixed Bottom Section */}
      <div
        style={{
          position: "fixed",
          bottom: 60,
          left: 0,
          right: 0,
          background: "#fff",
          padding: "16px",
          borderTop: "1px solid #eee",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Amount Input */}
          <Input
            type="number"
            placeholder="Enter amount"
            bordered={false}
            value={formData.amount}
            onChange={(e) => handleChange("amount", e.target.value)}
            style={{
              flex: 1,
              fontSize: 20,
              fontWeight: 600,
              padding: "10px 14px",
              borderRadius: 12,
              background: "#f5f5f5",
            }}
          />

          {/* Send Button */}
          <Button
            shape="circle"
            type="primary"
            size="large"
            icon={<ArrowRightOutlined />}
            onClick={() => handleNext("sendMoney")} // Attach your handler here
            style={{ width: 56, height: 56, fontSize: 20 }}
          />
        </div>

        {/* Fee text */}
        <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 8 }}>
          {formData.amount
            ? "Fees : " +
              calculateTransferFee(
                settingDetails.feeConfig.find(
                  (e) => e.customerType === customerType
                ),
                Number(formData.amount)
              )
            : "No Fee"}
        </div>
      </div>
    </div>
  );

  const renderRequestMoney = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#f9f9f9",
      }}
    >
      <Card
        style={{
          maxWidth: 430,
          borderRadius: 18,
          flex: 1,
        }}
      >
        <LeftOutlined
          style={{ fontSize: 18, cursor: "pointer", marginRight: 12 }}
          onClick={() => setActiveTab("home")}
        />

        {/* Header */}
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 18 }}>
          Request Money from
        </div>

        <hr />

        <div
          onClick={() => openDrawer("request")}
          style={{
            border: "1px solid #e6eaf0",
            borderRadius: 8,
            padding: 10,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            marginBottom: 16,
          }}
        >
          <span style={{ flex: 1, color: formData.payee ? "#222" : "#bbb" }}>
            {formData.payee ? formData.payee.name : "Phone number"}
          </span>
          <DownOutlined />
        </div>
        <br />

        {/* Inputs */}
        <Input
          placeholder="Request Reason"
          bordered={false}
          value={formData.requestReason}
          onChange={(e) => handleChange("requestReason", e.target.value)}
          style={{
            marginBottom: 14,
            borderRadius: 12,
            background: "#f5f5f5",
            padding: "10px 14px",
          }}
        />
      </Card>

      {/* Fixed Bottom Section */}
      <div
        style={{
          position: "fixed",
          bottom: 60,
          left: 0,
          right: 0,
          background: "#fff",
          padding: "16px",
          borderTop: "1px solid #eee",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Amount Input */}
          <Input
            type="number"
            placeholder="Enter amount"
            bordered={false}
            value={formData.amount}
            onChange={(e) => handleChange("amount", e.target.value)}
            style={{
              flex: 1,
              fontSize: 20,
              fontWeight: 600,
              padding: "10px 14px",
              borderRadius: 12,
              background: "#f5f5f5",
            }}
          />

          {/* Send Button */}
          <Button
            shape="circle"
            type="primary"
            size="large"
            icon={<ArrowRightOutlined />}
            onClick={() => handleNext("requestMoney")}
            style={{ width: 56, height: 56, fontSize: 20 }}
          />
        </div>
      </div>
    </div>
  );

  const renderDrawer = (type) => (
    <Drawer
      placement="bottom"
      open={drawerVisible === type}
      onClose={handleDrawerClose}
      height="90vh" // ✅ responsive height
      closable={false}
      style={{
        borderRadius: "18px 18px 0 0",
        overflow: "hidden", // prevent double scroll
      }}
      bodyStyle={{
        padding: "16px",
        overflowY: "auto", // ✅ scroll if content is longer
        maxHeight: "calc(90vh - 60px)", // keeps space for header
      }}
    >
      {/* Sticky Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "sticky", // ✅ stays visible while scrolling
          top: 0,
          background: "#fff",
          zIndex: 10,
          paddingBottom: 12,
          marginBottom: 12,
        }}
      >
        <Text strong style={{ fontSize: 18 }}>
          {type === "payee" ? "Money to" : "Request from"}
        </Text>
        <CloseOutlined
          onClick={handleDrawerClose}
          style={{
            position: "absolute",
            right: 0,
            fontSize: 18,
            cursor: "pointer",
            color: "#555",
          }}
        />
      </div>

      {/* Search Input */}
      <Input
        placeholder="Phone number or payee name"
        onChange={(e) => debouncedSearch(e.target.value)}
        style={{
          width: "100%",
          marginBottom: 20,
          borderRadius: 24,
          border: "none",
          background: "#f5f5f5",
          padding: "10px 16px",
        }}
      />

      {/* List */}
      <List
        dataSource={customerList}
        renderItem={(item) => (
          <List.Item
            onClick={() => handlePayeeSelect(item)}
            style={{ cursor: "pointer" }}
          >
            <List.Item.Meta
              avatar={<Avatar size={40}>{item.name?.[0]}</Avatar>}
              title={item.name}
              description={item.mobileNo}
            />
          </List.Item>
        )}
      />
    </Drawer>
  );

  return (
    <>
      {showNext === "sendMoney" && (
        <SendMoneyConfirm
          data={{
            to: formData.payee?.name || "Unknown",
            payId: formData.payee?.mobileNo || "-",
            from: userDetails.name,
            category: formData.category || "-",
            amount: `${formData.amount}`,
            fee: calculateTransferFee(
              settingDetails.feeConfig.find(
                (e) => e.customerType === customerType
              ),
              Number(formData.amount)
            ),
          }}
          onBack={() => {
            setActiveTab("payany1");
            setShowNext("");
          }}
          onConfirm={() => {
            handleSendMoney(selectedPayee.mobileNo, formData.amount);
          }}
        />
      )}

      {showNext === "requestMoney" && (
        <RequestMoneyConfirm
          data={{
            to: formData.payee?.name || "Unknown",
            payId: formData.payee?.mobileNo || "-",
            requestReason: formData.requestReason,
            amount: `${formData.amount}`,
          }}
          onBack={() => {
            setActiveTab("request-money");
            setShowNext("");
          }}
          onConfirm={() => {
            moneyRequestSent();

            console.log("Money requested!");
          }}
        />
      )}
      {activeTab === "home" && renderMainMenu()}

      {activeTab === "payany1" && renderPayany1Form()}

      {activeTab === "request-money" && renderRequestMoney()}

      {activeTab === "view-payment-requests" && (
        <MoneyRequests
          onBack={() => setActiveTab("home")}
          getUserDetails={() => getUserDetails()}
        />
      )}
      {activeTab === "requestMoney" && (
        <MoneyRequestSuccess
          onHome={() => setActiveTab("home")}
          onViewRequests={() => {
            setActiveTab("view-payment-requests");
          }}
          selectedPayee={selectedPayee}
          amount={formData.amount}
        />
      )}

      {activeTab === "sendMoneySuccess" && (
        <SendMoneySuccess
          onHome={() => setActiveTab("home")}
          onViewTransaction={(e) => setParentTab(e)}
          selectedPayee={selectedPayee}
          amount={formData.amount}
        />
      )}
      {renderDrawer("payee")}
      {renderDrawer("request")}

      {activeTab === "wallets" && (
        <WalletList
          onBack={() => setActiveTab("home")}
          walletList={walletList}
          getUserDetails={getUserDetails}
        />
      )}
    </>
  );
};

export default PaymentScreen;
